/* eslint-disable @typescript-eslint/no-explicit-any */
import { hasConsent } from 'utils/attribution/consent';
import { isEUVisitor } from 'utils/geo/region';

type Props = Record<string, any>;
type Options = Record<string, any>;

type QueuedEvent = {
  type: 'track' | 'identify' | 'page';
  args: any[];
  // Per-provider delivery flags. An event stays in the queue until both
  // flags are false. Without this we lose events submitted when one
  // provider is initialized but the other is still loading (e.g. form
  // submissions firing before Rudder finishes loading drop their Rudder
  // delivery, which broke /request-a-demo/ lead notifications in Slack).
  needsSegment: boolean;
  needsRudder: boolean;
};

const eventQueue: QueuedEvent[] = [];
let isProcessingQueue = false;

const requiresConsent = (): boolean => isEUVisitor() && !hasConsent('marketingAndAnalytics');

const seg = () => (typeof window !== 'undefined' ? (window as any).analytics : undefined);
const rud = () => (typeof window !== 'undefined' ? (window as any).rudderanalytics : undefined);

const isSegmentInitialized = (): boolean => {
  const analytics = seg();

  return !!(analytics && typeof analytics.track === 'function' && analytics.initialized);
};

const isRudderInitialized = (): boolean => {
  const rudder = rud();

  if (!rudder || Array.isArray(rudder)) {
    return false;
  }

  try {
    const instance = rudder.getAnalyticsInstance?.();

    return instance?.initialized === true;
  } catch {
    return false;
  }
};

const deliverToSegment = (event: QueuedEvent): boolean => {
  if (!event.needsSegment) {
    return true;
  }
  const analytics = seg();
  if (!analytics) {
    return false;
  }
  try {
    switch (event.type) {
      case 'track':
        analytics.track?.(...event.args);
        break;
      case 'identify':
        analytics.identify?.(...event.args);
        break;
      case 'page':
        analytics.page?.(...event.args);
        break;
    }

    return true;
  } catch (error) {
    console.warn(`Segment ${event.type} error:`, error);

    return true; // give up on this event for segment, do not retry forever
  }
};

const deliverToRudder = (event: QueuedEvent): boolean => {
  if (!event.needsRudder) {
    return true;
  }
  const rudder = rud();
  if (!rudder || Array.isArray(rudder)) {
    return false;
  }
  try {
    switch (event.type) {
      case 'track':
        rudder.track?.(...event.args);
        break;
      case 'identify':
        rudder.identify?.(...event.args);
        break;
      case 'page':
        rudder.page?.(...event.args);
        break;
    }

    return true;
  } catch (error) {
    console.warn(`Rudder ${event.type} error:`, error);

    return true;
  }
};

const processQueue = (): void => {
  if (isProcessingQueue || eventQueue.length === 0) {
    return;
  }

  const segReady = isSegmentInitialized();
  const rudReady = isRudderInitialized();

  if (!segReady && !rudReady) {
    return;
  }

  isProcessingQueue = true;

  const remaining: QueuedEvent[] = [];
  while (eventQueue.length > 0) {
    const event = eventQueue.shift();
    if (!event) {
      break;
    }

    if (segReady && deliverToSegment(event)) {
      event.needsSegment = false;
    }
    if (rudReady && deliverToRudder(event)) {
      event.needsRudder = false;
    }

    if (event.needsSegment || event.needsRudder) {
      remaining.push(event);
    }
  }

  if (remaining.length) {
    eventQueue.push(...remaining);
  }

  isProcessingQueue = false;
};

if (typeof window !== 'undefined') {
  const checkInterval = setInterval(() => {
    const segReady = isSegmentInitialized();
    const rudReady = isRudderInitialized();

    if (segReady || rudReady) {
      processQueue();

      if ((segReady && rudReady) || eventQueue.length === 0) {
        clearInterval(checkInterval);
      }
    }
  }, 500);

  // Segment/Rudder now start loading on the first interaction, which can
  // happen well after page load, so keep retrying queued events longer
  // than the old 30s window before giving up.
  setTimeout(() => clearInterval(checkInterval), 120000);
}

const dispatch = (type: QueuedEvent['type'], args: any[]): void => {
  if (requiresConsent()) {
    return;
  }

  const event: QueuedEvent = { type, args, needsSegment: true, needsRudder: true };

  if (isSegmentInitialized() && deliverToSegment(event)) {
    event.needsSegment = false;
  }
  if (isRudderInitialized() && deliverToRudder(event)) {
    event.needsRudder = false;
  }

  // If either provider still hasn't received the event, queue it. The
  // background interval (or future track calls) will retry delivery once
  // the slower provider finishes initializing.
  if (event.needsSegment || event.needsRudder) {
    eventQueue.push(event);
  }
};

export const analyticsClient = {
  isSegmentReady: (): boolean => isSegmentInitialized(),
  isRudderReady: (): boolean => isRudderInitialized(),

  track: (event: string, props?: Props, options?: Options) => {
    dispatch('track', [event, props, options]);
  },

  identify: (userIdOrEmail: string, traits?: Props, options?: Options) => {
    dispatch('identify', [userIdOrEmail, traits, options]);
  },

  page: (name?: string, props?: Props, options?: Options) => {
    dispatch('page', [name, props, options]);
  },
};
